import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../utils/axiosConfig";
import { getSocket } from "../utils/socket"; // Thay thế initializeSocket bằng getSocket
import ChatWidgetParticipants from "../components/ChatWidgetParticipants";
import Navbar from "../components/Navbar";
import MatchesList from "../components/MatchesList";
import FriendsList from "../components/FriendsList";
import UserSuggestionsList from "../components/UserSuggestionsList";

const SearchAndSuggest = () => {
  const { user: authUser, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    gender: "",
    city: "",
    country: "",
    minAge: "",
    maxAge: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [matches, setMatches] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendsList, setFriendsList] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingLike, setIsLoadingLike] = useState(false);
  const [isLoadingFriend, setIsLoadingFriend] = useState(false);
  const [likeStates, setLikeStates] = useState({});
  const [friendRequestStates, setFriendRequestStates] = useState({});
  const [chatUsers, setChatUsers] = useState([]);
  const usersPerPage = 48;

  const fetchAllUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const query = new URLSearchParams({
        ...filters,
        page: currentPage,
        limit: usersPerPage,
      }).toString();
      const res = await api.get(`/api/users/all-users?${query}`);
      setUsers(res.data.users || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error("Lỗi khi lấy danh sách người dùng:", error.response || error);
      setError(
        error.response?.data?.message ||
          "Không thể tải danh sách người dùng. Vui lòng kiểm tra kết nối."
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage]);

  const fetchMatches = useCallback(async () => {
    try {
      const res = await api.get("/api/users/matches");
      const fetchedMatches = res.data.matches || [];
      setMatches(fetchedMatches);
      const initialLikeStates = fetchedMatches.reduce((acc, match) => {
        const likedUserId = match.likedUserId._id
          ? String(match.likedUserId._id)
          : String(match.likedUserId);
        if (String(match.userId._id || match.userId) === String(authUser._id)) {
          acc[likedUserId] = true;
        }
        return acc;
      }, {});
      setLikeStates(initialLikeStates);
    } catch (error) {
      console.error("Lỗi fetchMatches:", error.response ? error.response.data : error.message);
    }
  }, [authUser]);

  const fetchFriendRequests = useCallback(async () => {
    try {
      const res = await api.get("/api/users/friend-requests");
      setFriendRequests(res.data.requests || []);
      const initialFriendRequestStates = res.data.requests.reduce((acc, req) => {
        if (
          authUser &&
          authUser._id &&
          String(req.senderId._id || req.senderId) === String(authUser._id) &&
          req.status === "pending"
        ) {
          acc[req.receiverId._id || String(req.receiverId)] = true;
        }
        return acc;
      }, {});
      setFriendRequestStates(initialFriendRequestStates);
    } catch (error) {
      console.error(
        "Lỗi fetchFriendRequests:",
        error.response ? error.response.data : error.message
      );
    }
  }, [authUser]);

  const fetchFriends = useCallback(async () => {
    try {
      const res = await api.get("/api/users/friends");
      const fetchedFriends = res.data.friends || [];
      setFriends(fetchedFriends);
    } catch (error) {
      console.error("Lỗi fetchFriends:", error.response ? error.response.data : error.message);
    }
  }, []);

  const fetchFriendsList = useCallback(async () => {
    try {
      const timestamp = new Date().getTime();
      const friendsRes = await api.get(`/api/users/friends?t=${timestamp}`);
      const confirmedFriends = friendsRes.data.friends || [];

      const pendingRes = await api.get("/api/users/friend-requests");
      const pendingRequests = (pendingRes.data.requests || []).filter(
        (req) =>
          String(req.receiverId._id || req.receiverId) === String(authUser._id) &&
          req.status === "pending"
      );

      const confirmedIds = new Set(confirmedFriends.map((f) => f._id.toString()));
      const pendingFriends = pendingRequests
        .map((req) => ({
          ...req.senderId,
          isPending: true,
          requestId: req._id,
        }))
        .filter((p) => !confirmedIds.has(p._id.toString()));

      const uniqueFriendsList = [...confirmedFriends, ...pendingFriends].reduce((acc, friend) => {
        const friendId = friend.isPending ? `pending-${friend.requestId}` : `confirmed-${friend._id}`;
        if (!acc.some((f) => (f.isPending ? `pending-${f.requestId}` : `confirmed-${f._id}`) === friendId)) {
          acc.push(friend);
        }
        return acc;
      }, []);

      setFriendsList(uniqueFriendsList);
    } catch (error) {
      console.error("Lỗi tải danh sách bạn bè:", error);
    }
  }, [authUser]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/api/users/notifications");
      setNotifications(res.data);
      const unread = res.data.filter((n) => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Lỗi khi lấy thông báo:", error);
    }
  }, []);

  useEffect(() => {
    if (authUser) {
      const fetchData = async () => {
        await fetchFriends();
        await fetchAllUsers();
        await Promise.all([
          fetchMatches(),
          fetchFriendRequests(),
          fetchFriendsList(),
          fetchNotifications(),
        ]);
      };

      fetchData();

      const pollInterval = setInterval(fetchNotifications, 10000);

      const socket = getSocket(); // Sử dụng getSocket thay vì initializeSocket
      if (!socket.connected) {
        socket.connect();
      }

      socket.on("connect", () => {
        console.log("Kết nối WebSocket thành công:", socket.id);
        socket.emit("joinRoom", authUser._id);
      });

      socket.on("notification", (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
      });

      return () => {
        clearInterval(pollInterval);
      };
    }
  }, [
    authUser,
    token,
    fetchAllUsers,
    fetchMatches,
    fetchFriendRequests,
    fetchFriends,
    fetchFriendsList,
    fetchNotifications,
  ]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleLike = async (likedUserId) => {
    if (isLoadingLike) return;
    setIsLoadingLike(true);
    try {
      if (!authUser || !authUser._id) {
        alert("Người dùng chưa được tải, vui lòng thử lại!");
        return;
      }
      const isAlreadyLiked = likeStates[likedUserId];
      if (isAlreadyLiked) {
        alert("Bạn đã Like người dùng này!");
        return;
      }
      setLikeStates((prev) => ({ ...prev, [likedUserId]: true }));
      const response = await api.put("/api/users/like", { likedUserId: String(likedUserId) });
      alert(response.data.message || "Like thành công!");
      await Promise.all([fetchMatches(), fetchNotifications()]);
    } catch (error) {
      console.error("Lỗi khi gửi Like:", error.response?.data || error.message);
      setLikeStates((prev) => ({ ...prev, [likedUserId]: false }));
      alert("Lỗi khi gửi Like: " + (error.response?.data?.message || "Không xác định"));
    } finally {
      setIsLoadingLike(false);
    }
  };

  const handleUnlike = async (likedUserId) => {
    if (isLoadingLike) return;
    setIsLoadingLike(true);
    try {
      if (!authUser || !authUser._id) {
        alert("Người dùng chưa được tải, vui lòng thử lại!");
        return;
      }
      const isLiked = likeStates[likedUserId];
      if (!isLiked) {
        alert("Bạn chưa Like người dùng này!");
        return;
      }
      setLikeStates((prev) => ({ ...prev, [likedUserId]: false }));
      setMatches((prev) =>
        prev.filter((m) => String(m.likedUserId._id || m.likedUserId) !== likedUserId)
      );
      const response = await api.delete("/api/users/unlike", {
        data: { likedUserId: String(likedUserId) },
      });
      alert(response.data.message || "Unlike thành công!");
      await Promise.all([fetchMatches(), fetchNotifications()]);
    } catch (error) {
      console.error("Lỗi khi hủy Like:", error.response?.data || error.message);
      setLikeStates((prev) => ({ ...prev, [likedUserId]: true }));
      alert("Lỗi khi hủy Like: " + (error.response?.data?.message || "Không xác định"));
    } finally {
      setIsLoadingLike(false);
    }
  };

  const sendFriendRequest = async (receiverId) => {
    if (isLoadingFriend) return;
    setIsLoadingFriend(true);
    try {
      if (!authUser || !authUser._id) {
        alert("Người dùng chưa được tải, vui lòng thử lại!");
        return;
      }
      setFriendRequestStates((prev) => ({ ...prev, [receiverId]: true }));
      const response = await api.put("/api/users/friend-request", { receiverId });
      if (response.status === 200) {
        alert(response.data.message);
        await fetchFriendRequests();
      } else {
        setFriendRequestStates((prev) => ({ ...prev, [receiverId]: false }));
        alert("Lỗi khi gửi Friend Request: " + response.data.message);
      }
    } catch (error) {
      console.error("Lỗi khi gửi Friend Request:", error.response ? error.response.data : error);
      setFriendRequestStates((prev) => ({ ...prev, [receiverId]: false }));
      alert("Lỗi khi gửi Friend Request: " + (error.response?.data?.message || error.message));
    } finally {
      setIsLoadingFriend(false);
    }
  };

  const cancelFriendRequest = async (receiverId) => {
    if (isLoadingFriend) return;
    setIsLoadingFriend(true);
    try {
      if (!authUser || !authUser._id) {
        alert("Người dùng chưa được tải, vui lòng thử lại!");
        return;
      }
      setFriendRequestStates((prev) => ({ ...prev, [receiverId]: false }));
      setFriendRequests((prev) =>
        prev.filter((req) => String(req.receiverId) !== String(receiverId))
      );
      const response = await api.put("/api/users/cancel-friend-request", { receiverId });
      if (response.status === 200) {
        alert(response.data.message);
        await fetchFriendRequests();
      } else {
        setFriendRequestStates((prev) => ({ ...prev, [receiverId]: true }));
        setFriendRequests((prev) => [
          ...prev,
          { senderId: authUser._id, receiverId: receiverId, status: "pending" },
        ]);
        alert("Lỗi khi hủy Friend Request: " + response.data.message);
      }
    } catch (error) {
      console.error("Lỗi khi hủy Friend Request:", error.response ? error.response.data : error);
      setFriendRequestStates((prev) => ({ ...prev, [receiverId]: true }));
      setFriendRequests((prev) => [
        ...prev,
        { senderId: authUser._id, receiverId: receiverId, status: "pending" },
      ]);
      alert("Lỗi khi hủy Friend Request: " + (error.response?.data?.message || error.message));
    } finally {
      setIsLoadingFriend(false);
    }
  };

  const respondFriendRequest = async (requestId, action) => {
    try {
      const response = await api.put(`/api/users/friend-request/${requestId}`, { action });
      if (response.status === 200) {
        alert(response.data.message);
        await fetchFriendRequests();
        if (action === "accept") {
          await fetchFriends();
          await fetchFriendsList();
          await fetchAllUsers();
        }
      }
    } catch (error) {
      console.error(
        "Lỗi chi tiết khi phản hồi Friend Request:",
        error.response ? error.response.data : error
      );
      alert(
        `Lỗi khi phản hồi Friend Request: ${
          error.response?.data?.message || error.message || "Không xác định"
        }`
      );
    }
  };

  const respondMatchRequest = async (matchId, action) => {
    try {
      const response = await api.put(`/api/users/match-request/${matchId}`, { action });
      if (response.status === 200) {
        alert(response.data.message);
        const updatedMatches = await api.get("/api/users/matches");
        setMatches(updatedMatches.data.matches || []);
        if (action === "accept") {
          await fetchNotifications();
        }
      }
    } catch (error) {
      console.error(
        "Lỗi chi tiết khi phản hồi Match Request:",
        error.response ? error.response.data : error
      );
      alert(
        `Lỗi khi phản hồi Match Request: ${
          error.response?.data?.message || error.message || "Không xác định"
        }`
      );
    }
  };

  const removeFriend = async (friendId) => {
    try {
      const response = await api.put("/api/users/remove-friend", { friendId });
      if (response.status === 200) {
        alert(response.data.message);
        await fetchFriends();
        await fetchFriendsList();
        await fetchAllUsers();
      }
    } catch (error) {
      console.error("Lỗi khi hủy kết bạn:", error);
      alert("Lỗi khi hủy kết bạn!");
    }
  };

  const handleToggleNotifications = async (notificationId, action) => {
    if (!token) return;

    try {
      if (action === "read") {
        const res = await api.put(`/api/users/notifications/${notificationId}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 200) {
          setNotifications((prev) =>
            prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
          );
          setUnreadCount((prev) => prev - 1);
        }
      } else if (action === "delete") {
        const res = await api.delete(`/api/users/notifications/${notificationId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 200) {
          setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
          setUnreadCount((prev) => {
            const deletedNotification = notifications.find((n) => n._id === notificationId);
            return deletedNotification && !deletedNotification.read ? prev - 1 : prev;
          });
        }
      }
    } catch (error) {
      console.error(`Lỗi khi ${action} thông báo:`, error);
    }
  };

  const handleOpenChat = async (user) => {
    if (!user || !authUser || !authUser._id) {
      throw new Error("Không thể mở chat. Dữ liệu người dùng không hợp lệ!");
    }
    const isValidUser = [...users, ...friendsList].some(
      (u) => u._id && String(u._id) === String(user._id)
    );
    if (!isValidUser) {
      throw new Error("Người dùng không tồn tại trong danh sách!");
    }
    setChatUsers((prev) => [...new Set([...prev, user._id])]);
  };
  

  if (!authUser) {
    return <p className="text-center text-gray-600">Đang tải...</p>;
  }

  return (
    <div className="font-sans min-h-screen bg-gray-100">
      <Navbar
        unreadCount={unreadCount}
        unreadNotificationCount={unreadCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />

      <div className="pt-20 max-w-7xl mx-auto p-6">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          Danh sách người dùng
        </h2>

        <div className="mb-6 p-4 border rounded-lg shadow-sm bg-gray-50">
          <h3 className="text-lg font-semibold mb-2">Lọc người dùng</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Giới tính:</label>
              <select
                name="gender"
                value={filters.gender}
                onChange={handleFilterChange}
                className="w-full border p-2 rounded shadow-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tất cả</option>
                <option value="male">Nam</option>
                <option value="female">Nữ</option>
                <option value="other">Khác</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Thành phố:</label>
              <input
                type="text"
                name="city"
                value={filters.city}
                onChange={handleFilterChange}
                placeholder="Nhập thành phố..."
                className="w-full border p-2 rounded shadow-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Quốc gia:</label>
              <input
                type="text"
                name="country"
                value={filters.country}
                onChange={handleFilterChange}
                placeholder="Nhập quốc gia..."
                className="w-full border p-2 rounded shadow-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tuổi tối thiểu:</label>
              <input
                type="number"
                name="minAge"
                value={filters.minAge}
                onChange={handleFilterChange}
                placeholder="Tuổi min..."
                className="w-full border p-2 rounded shadow-sm focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tuổi tối đa:</label>
              <input
                type="number"
                name="maxAge"
                value={filters.maxAge}
                onChange={handleFilterChange}
                placeholder="Tuổi max..."
                className="w-full border p-2 rounded shadow-sm focus:ring-2 focus:ring-blue-500"
                min="0"
              />
            </div>
          </div>
        </div>

        <UserSuggestionsList
          users={users}
          loading={loading}
          error={error}
          authUser={authUser}
          likeStates={likeStates}
          friendRequestStates={friendRequestStates}
          matches={matches}
          friendRequests={friendRequests}
          friends={friends}
          filters={filters}
          handleLike={handleLike}
          handleUnlike={handleUnlike}
          sendFriendRequest={sendFriendRequest}
          cancelFriendRequest={cancelFriendRequest}
          handleOpenChat={handleOpenChat}
          isLoadingLike={isLoadingLike}
          isLoadingFriend={isLoadingFriend}
        />

        {users.length > 0 && (
          <div className="mt-6 flex justify-center items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              Trước
            </button>
            <span className="text-gray-700">Trang {currentPage} / {totalPages}</span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              Tiếp theo
            </button>
          </div>
        )}

        <MatchesList
          matches={matches}
          loading={loading}
          authUser={authUser}
          respondMatchRequest={respondMatchRequest}
        />

        <FriendsList
          friendsList={friendsList}
          loading={loading}
          respondFriendRequest={respondFriendRequest}
          removeFriend={removeFriend}
          handleOpenChat={handleOpenChat}
        />

        {chatUsers.length > 0 && (
          <ChatWidgetParticipants
            onSelectChat={chatUsers[chatUsers.length - 1]}
            onClose={() => setChatUsers([])}
            users={users}
            friendsList={friendsList}
          />
        )}
      </div>
    </div>
  );
};

export default SearchAndSuggest;